import { useChartRenderStore } from "@/stores/ChartRenderStore";
import { computed, ref } from "vue";


export function useChartRender(chart) {

  // --------------------------------------------------------
  // CONSTANTS 
  // --------------------------------------------------------

  const chartRenderStore = useChartRenderStore()

  // --------------------------------------------------------
  // STATE 
  // --------------------------------------------------------

  const currentTime = ref(0)

  const chartPositionY = computed(() => -chartRenderStore.timeToPixel(currentTime.value))
  const chartPositionX = ref(0)

  const stageHeight = ref(800)
  const stageWidth = ref(600)

  // --------------------------------------------------------
  // GETTERS 
  // --------------------------------------------------------

  const chartHeight = computed(() => chart.duration.value <= 0 ? 1 : chartRenderStore.timeToPixel(chart.duration.value))
  const chartWidth = computed(() => chart.columnCount.value * chartRenderStore.columnWidth)

  const vScrollBarHeight = computed(() => Math.max(chartRenderStore.SCROLLBAR_SIZE, stageHeight.value / chartHeight.value * stageHeight.value))
  const hScrollBarWidth = computed(() => Math.max(chartRenderStore.SCROLLBAR_SIZE, stageWidth.value / chartWidth.value * stageWidth.value))

  const vScrollBarX = computed(() => stageWidth.value - chartRenderStore.SCROLLBAR_PADDING - chartRenderStore.SCROLLBAR_SIZE)
  const vScrollBarY = computed(() => {
    const chartRange = chartHeight.value
    const scrollBarRange = stageHeight.value - vScrollBarHeight.value - chartRenderStore.SCROLLBAR_PADDING * 2

    return chartRange <= 0 ? chartRenderStore.SCROLLBAR_PADDING :
      -chartPositionY.value / chartRange * scrollBarRange + chartRenderStore.SCROLLBAR_PADDING
  })

  const hScrollBarY = computed(() => stageHeight.value - chartRenderStore.SCROLLBAR_PADDING - chartRenderStore.SCROLLBAR_SIZE)
  const hScrollBarX = computed(() => {
    const chartRange = chartWidth.value - stageWidth.value
    const scrollBarRange = stageWidth.value - hScrollBarWidth.value - chartRenderStore.SCROLLBAR_PADDING * 2

    return chartRange <= 0 ? chartRenderStore.SCROLLBAR_PADDING :
      -chartPositionX.value / chartRange * scrollBarRange + chartRenderStore.SCROLLBAR_PADDING
  })

  const negativeVisibleTime = computed(() => chartRenderStore.pixelToTime(chartRenderStore.hitPosY))
  const positiveVisibleTime = computed(() => chartRenderStore.pixelToTime(stageHeight.value - chartRenderStore.hitPosY))

  const screenToChartPixelX = (value) => (value - chartPositionX.value) / chartRenderStore.zoomX
  const screenToChartPixelY = (value) => ((chartRenderStore.reverseScroll ? stageHeight.value - value : value) - chartPositionY.value - chartRenderStore.hitPosY) / chartRenderStore.zoomY

  // lines
  const clTimePerBarline = computed(() => chart.orderedCL.value.map(v => 60 / v.eventLine.bpm))
  const clPixelPerBarline = computed(() => clTimePerBarline.value.map(v => chartRenderStore.timeToPixel(v)))
  const clPixelPerSubdivision = computed(() => clTimePerBarline.value.map(v => chartRenderStore.timeToPixel(v / chartRenderStore.subdivision)))

  const controlLinesBarLinePositions = computed(() => {
    return clVisibleBeats.value.map((e, clIndex) => {
      if (e == null) return null
      const start = Math.trunc(e[0])
      const end = Math.trunc(e[1])
      const t = chartRenderStore.timeToPixel(chart.orderedCL.value[clIndex].eventLine.time)
      return Array.from({ length: end - start }, (_, i) => (start + i + 1) * clPixelPerBarline.value[clIndex] + t)
    })
  })

  const controlLinesSubdivisionPositions = computed(() => {
    let res = []
    chart.orderedCL.value.forEach((_cl, index) => {
      res.push([...Array(chartRenderStore.subdivision - 1).keys()].map(x => (x + 1) * clPixelPerSubdivision[index]))
    })
    return res
  })

  // visibility
  const clVisibleBeats = computed(() => {
    const minTy = currentTime.value - negativeVisibleTime.value
    const maxTy = currentTime.value + positiveVisibleTime.value

    const noteTimeHeight = chartRenderStore.pixelToTime(chartRenderStore.noteHeight)

    const res = []

    for (const cl of chart.orderedCL.value) {
      if (minTy >= cl.endTime || maxTy <= cl.eventLine.time) {
        res.push(null)
        continue
      }
      const minBeat = minTy - noteTimeHeight > cl.eventLine.time ? chart.getBeatFromTime(minTy - noteTimeHeight, cl.eventLine.eventLineId) : 0
      const maxBeat = maxTy < cl.endTime ? chart.getBeatFromTime(maxTy, cl.eventLine.eventLineId) : cl.durationInBeats
      res.push([minBeat, maxBeat])
    }
    return res
  })

  // get positions
  const getCellFromPosition = (x, y) => {
    if (chart.orderedCL.value.length <= 0) return null;

    const cellX = Math.trunc(x / chartRenderStore.columnWidth)
    if (cellX < 0 || cellX >= chart.columnCount.value) return null;

    const ty = y / 1000

    if (chart.duration.value < ty || ty < 0) return null;
    let i = 0;
    while (chart.orderedCL.value[i].endTime <= ty) i++

    const cellY = Math.trunc((ty - chart.orderedCL.value[i].eventLine.time) / (clTimePerBarline.value[i] / chartRenderStore.subdivision))

    return { clIndex: i, column: cellX, numerator: cellY, denominator: chartRenderStore.subdivision }
  }

  const getClosestVisibleCell = (x, y) => {
    if (chart.orderedCL.value.length <= 0) return null;
    const columnIndex = Math.trunc(x / chartRenderStore.columnWidth)
    if (columnIndex < 0 || columnIndex >= chart.columnCount.value) return null;

    const ty = y / 1000
    const timeThreshold = chartRenderStore.pixelToTime(chartRenderStore.mouseClickEffectpixelRange)

    if (ty + timeThreshold < 0 || ty - timeThreshold >= chart.duration.value) return null

    let clIndex = null
    let numerator = null

    if (ty <= 0) {
      clIndex = 0
      numerator = 0
    } else if (ty >= chart.duration.value) {
      clIndex = chart.orderedCL.value.length - 1;
      const clTime = chart.orderedCL.value[clIndex].eventLine.time

      const timePerSubdivision = clTimePerBarline.value[clIndex] / chartRenderStore.subdivision
      numerator = Math.trunc((chart.duration.value - clTime) / timePerSubdivision)
      const previousSubdivisionTime = numerator * timePerSubdivision + clTime
      if (ty - previousSubdivisionTime > timeThreshold) return null
    } else {
      clIndex = 0;
      while (chart.orderedCL.value[clIndex].endTime <= ty) clIndex++
      const clTime = chart.orderedCL.value[clIndex].eventLine.time

      const timePerSubdivision = clTimePerBarline.value[clIndex] / chartRenderStore.subdivision
      numerator = Math.trunc((ty - clTime) / timePerSubdivision)
      const previousSubdivisionTime = numerator * timePerSubdivision + clTime
      const nextSubdivisionTime = (numerator + 1) * timePerSubdivision + clTime

      if (clIndex < chart.orderedCL.value.length - 1 && chart.orderedCL.value[clIndex + 1].eventLine.time - ty < ty - previousSubdivisionTime) {
        //next control line is closer than previous subdivision
        if (chart.orderedCL.value[clIndex + 1].eventLine.time - ty > timeThreshold) return null
        clIndex++
        numerator = 0
      } else if (nextSubdivisionTime < chart.duration.value && nextSubdivisionTime - ty < ty - previousSubdivisionTime) {
        //next subdivision is closer than previous subdivision
        if (nextSubdivisionTime - ty > timeThreshold) return null
        numerator++
      } else {
        //previous subdivision is the closest
        if (ty - previousSubdivisionTime > timeThreshold) return null
      }
    }

    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const v = gcd(numerator, chartRenderStore.subdivision)
    const eventLine = chart.orderedCL.value[clIndex].eventLine

    return { eventLine, columnIndex, numerator: numerator / v, denominator: chartRenderStore.subdivision / v }
  }

  const getClosestYNote = (x, y) => {
    if (chart.orderedCL.value.length <= 0) return null;

    const columnIndex = Math.trunc(x / chartRenderStore.columnWidth)
    if (columnIndex < 0 || columnIndex >= chart.columnCount.value) return null;

    const timeThreshold = chartRenderStore.pixelToTime(chartRenderStore.mouseClickEffectpixelRange)

    const ty = y / 1000
    const minTy = Math.max(ty - timeThreshold, 0)
    const maxTy = Math.min(ty + timeThreshold, chart.duration.value)

    if (ty + timeThreshold < 0 || ty - timeThreshold >= chart.duration.value) return null

    let bestCandidate = null

    for (const cl of chart.orderedCL.value) {
      if (minTy >= cl.endTime) continue
      if (maxTy <= cl.eventLine.time) break
      const minBeat = minTy > cl.eventLine.time ? chart.getBeatFromTime(minTy, cl.eventLine.eventLineId) : 0
      const maxBeat = maxTy < cl.endTime ? chart.getBeatFromTime(maxTy, cl.eventLine.eventLineId) : cl.durationInBeats

      const clNotes = chart.notesByEventLine.get(cl.eventLine.eventLineId)
      for (const beatNotes of clNotes.rangeQuery(minBeat, maxBeat)) {
        const n = beatNotes.find(e => e.column.columnIndex == columnIndex)
        if (!n) continue

        const candidate = n
        const candidateTime = chart.getTimeFromBeat(candidate.beat, candidate.eventLine.eventLineId)
        if (!bestCandidate) {
          bestCandidate = [Math.abs(candidateTime - ty), candidate]
          continue
        }

        if (Math.abs(candidateTime - ty) > bestCandidate[0]) {
          return bestCandidate[1]
        }

        bestCandidate = [Math.abs(candidateTime - ty), candidate]
      }
    }
    return bestCandidate?.[1] ?? null
  }

  //x1 y1 low point
  //x2 y2 high point
  const getNotesWithinRange = (x1, y1, x2, y2) => {
    x1 = Math.max(Math.min(x1, chartWidth.value), 0)
    x2 = Math.max(Math.min(x2, chartWidth.value), 0)
    const minTy = Math.max(Math.min(y1 / 1000, chart.duration.value), 0)
    const maxTy = Math.max(Math.min(y2 / 1000, chart.duration.value), 0)

    const columnIndex1 = Math.trunc(x1 / chartRenderStore.columnWidth)
    const columnIndex2 = Math.trunc(x2 / chartRenderStore.columnWidth)

    const beatsNotes = []

    for (const cl of chart.orderedCL.value) {
      if (minTy >= cl.endTime) continue
      if (maxTy <= cl.eventLine.time) break
      const minBeat = minTy > cl.eventLine.time ? chart.getBeatFromTime(minTy, cl.eventLine.eventLineId) : 0
      const maxBeat = maxTy < cl.endTime ? chart.getBeatFromTime(maxTy, cl.eventLine.eventLineId) : cl.durationInBeats

      const clNotes = chart.notesByEventLine.get(cl.eventLine.eventLineId)
      for (const beatNotes of clNotes.rangeQuery(minBeat, maxBeat)) {
        const n = beatNotes.filter(e => e.column.columnIndex >= columnIndex1 && e.column.columnIndex < columnIndex2)
        if (n.length <= 0) continue

        beatsNotes.push(n.map(e => e.noteId))
      }
    }
    return beatsNotes
  }

  // --------------------------------------------------------
  // ACTIONS
  // --------------------------------------------------------

  const setPositionX = (normalizedValue) => chartPositionX.value = -(chartWidth.value - stageWidth.value) * normalizedValue

  // --------------------------------------------------------
  // MISC
  // --------------------------------------------------------

  return {
    currentTime,
    getNotesWithinRange,
    chartWidth,
    chartHeight,
    stageHeight,
    stageWidth,
    vScrollBarX,
    vScrollBarY,
    hScrollBarX,
    hScrollBarY,
    vScrollBarHeight,
    hScrollBarWidth,
    chartPositionY,
    chartPositionX,
    setPositionX,
    negativeVisibleTime,
    positiveVisibleTime,
    screenToChartPixelY,
    screenToChartPixelX,
    getCellFromPosition,
    getClosestYNote,
    getClosestVisibleCell,
    controlLinesSubdivisionPositions,
    controlLinesBarLinePositions,
    clTimePerBarline,
    clPixelPerBarline,
    clPixelPerSubdivision,
    clVisibleBeats,
  }
}